/**
 * NetworkError - Error class for network-related failures
 * 
 * This error is thrown when network requests fail due to connectivity issues,
 * timeouts, or other network-related problems.
 */

export enum NetworkErrorType {
  TIMEOUT = 'timeout',
  OFFLINE = 'offline',
  CONNECTION = 'connection',
  UNKNOWN = 'unknown'
}

export class NetworkError extends Error {
  public readonly type: NetworkErrorType;
  public readonly originalError?: Error;

  constructor(message: string, type: NetworkErrorType = NetworkErrorType.UNKNOWN, originalError?: Error) {
    super(message);
    this.name = 'NetworkError';
    this.type = type;
    this.originalError = originalError;
  }

  /**
   * Check if this is a timeout error
   */
  isTimeout(): boolean {
    return this.type === NetworkErrorType.TIMEOUT;
  }

  /**
   * Check if this is due to being offline
   */
  isOffline(): boolean {
    return this.type === NetworkErrorType.OFFLINE;
  }

  /**
   * Check if this is a connection error
   */
  isConnectionError(): boolean {
    return this.type === NetworkErrorType.CONNECTION;
  }

  /**
   * Get user-friendly error message
   */
  getUserMessage(): string {
    switch (this.type) {
      case NetworkErrorType.TIMEOUT:
        return 'Request timed out. Please try again.';
      case NetworkErrorType.OFFLINE:
        return 'You appear to be offline. Please check your connection.';
      case NetworkErrorType.CONNECTION:
        return 'Network error. Please check your connection and try again.';
      default:
        return this.message ?? 'An unexpected network error occurred.';
    }
  }
}

/**
 * Convenience function for throwing network errors
 */
export function throwNetworkError(message: string, type: NetworkErrorType = NetworkErrorType.UNKNOWN, originalError?: Error): never {
  throw new NetworkError(message, type, originalError);
}
